"use client"

import { useState, useMemo } from "react"
import { Plus } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import {
  getPeriodData,
  FUEL_PRICE_USD,
  LITERS_PER_GALLON,
  DEMO_FUEL_PURCHASES,
  buildCumulativeChartData,
  type Period,
  type FuelPurchase,
} from "@/lib/demo-data"

type Unit = "liters" | "gallons"

const chartConfig = {
  estimated: { label: "Estimated Consumption", color: "var(--chart-1)" },
  purchased: { label: "Fuel Purchased", color: "var(--chart-5)" },
} satisfies ChartConfig

function conv(liters: number, unit: Unit) {
  return unit === "gallons" ? Math.round(liters / LITERS_PER_GALLON) : liters
}

function unitLabel(unit: Unit) {
  return unit === "gallons" ? "gal" : "L"
}

export default function DemoFuelPage() {
  const [period, setPeriod] = useState<Period>("month")
  const [unit, setUnit] = useState<Unit>("liters")
  const [purchases, setPurchases] = useState<FuelPurchase[]>(DEMO_FUEL_PURCHASES)
  const [showForm, setShowForm] = useState(false)
  const [formAmount, setFormAmount] = useState("")
  const [formNote, setFormNote] = useState("")
  const [formDate, setFormDate] = useState("2026-03-27")

  const data = getPeriodData(period)
  const periodLabels: Record<Period, string> = { today: "Today", week: "This Week", month: "This Month" }

  // Filter purchases by period
  const periodPurchases = useMemo(() => {
    if (period === "today") return purchases.filter((p) => p.date.getDate() === 27)
    if (period === "week") return purchases.filter((p) => p.date.getDate() >= 24)
    return purchases
  }, [purchases, period])

  const totalPurchased = periodPurchases.reduce((sum, p) => sum + p.liters, 0)
  const discrepancy = totalPurchased - data.totalFuel
  const discrepancyPct = data.totalFuel > 0 ? Math.round((discrepancy / data.totalFuel) * 100) : 0

  // Cumulative chart data
  const cumulativeData = useMemo(() => {
    const raw = buildCumulativeChartData(purchases)
    if (unit === "gallons") {
      return raw.map((d) => ({
        ...d,
        estimated: Math.round(d.estimated / LITERS_PER_GALLON),
        purchased: Math.round(d.purchased / LITERS_PER_GALLON),
      }))
    }
    return raw
  }, [purchases, unit])

  function handleAddPurchase() {
    if (!formAmount) return
    const amountLiters = unit === "gallons"
      ? Math.round(parseFloat(formAmount) * LITERS_PER_GALLON)
      : Math.round(parseFloat(formAmount))
    const newPurchase: FuelPurchase = {
      id: `fp-${Date.now()}`,
      date: new Date(formDate),
      liters: amountLiters,
      note: formNote || "Manual entry",
    }
    setPurchases((prev) => [...prev, newPurchase].sort((a, b) => a.date.getTime() - b.date.getTime()))
    setFormAmount("")
    setFormNote("")
    setShowForm(false)
  }

  return (
    <div className="space-y-6">
      {/* Header with toggles */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fuel Tracking</h1>
          <p className="text-sm text-muted-foreground">
            Estimated consumption vs fuel purchases · Interior diesel GYD $350/L (~${FUEL_PRICE_USD} USD)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Period toggle */}
          <div className="flex gap-1 rounded-lg border border-border/50 p-1">
            {(["today", "week", "month"] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  period === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
          {/* Unit toggle */}
          <div className="flex gap-1 rounded-lg border border-border/50 p-1">
            {(["liters", "gallons"] as Unit[]).map((u) => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  unit === u ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {u === "liters" ? "Liters" : "Gallons"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Estimated Consumption</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">
              {conv(data.totalFuel, unit).toLocaleString()} {unitLabel(unit)}
            </div>
            <p className="text-xs text-muted-foreground">${data.totalCost.toLocaleString()} · {periodLabels[period].toLowerCase()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Purchased</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">
              {conv(totalPurchased, unit).toLocaleString()} {unitLabel(unit)}
            </div>
            <p className="text-xs text-muted-foreground">
              {periodPurchases.length} purchase{periodPurchases.length !== 1 ? "s" : ""} · {periodLabels[period].toLowerCase()}
            </p>
          </CardContent>
        </Card>
        <Card className={discrepancy > 0 ? "border-red-500/20" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Discrepancy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold tabular-nums ${discrepancy > 0 ? "text-red-400" : "text-green-400"}`}>
              {discrepancy > 0 ? "+" : ""}{conv(discrepancy, unit).toLocaleString()} {unitLabel(unit)}
            </div>
            {discrepancy > 0 && (
              <p className="text-xs text-red-400/70">{discrepancyPct}% more purchased than consumed</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">GIS Monitoring</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">GYD $19,900</div>
            <p className="text-xs text-muted-foreground">~$95 USD/mo · all engines</p>
          </CardContent>
        </Card>
      </div>

      {/* Consumption vs Purchases chart */}
      {period === "month" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Consumption vs Purchases</CardTitle>
            <CardDescription>Cumulative {unitLabel(unit)} over March 2026</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <AreaChart data={cumulativeData} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  tickMargin={8}
                  tickFormatter={(v) => `Mar ${v}`}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  tickFormatter={(v) => `${v.toLocaleString()}`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <defs>
                  <linearGradient id="fillEstimated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-estimated)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="var(--color-estimated)" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="fillPurchased" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-purchased)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="var(--color-purchased)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Area
                  dataKey="estimated"
                  type="monotone"
                  fill="url(#fillEstimated)"
                  stroke="var(--color-estimated)"
                  strokeWidth={2}
                />
                <Area
                  dataKey="purchased"
                  type="stepAfter"
                  fill="url(#fillPurchased)"
                  stroke="var(--color-purchased)"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Engine fuel breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Engine Breakdown — {periodLabels[period]}</CardTitle>
          <CardDescription>Runtime × rated consumption</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Engine</TableHead>
                <TableHead className="text-right">Runtime</TableHead>
                <TableHead className="text-right">Fuel ({unitLabel(unit)})</TableHead>
                <TableHead className="text-right">Cost (USD)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.fuel.map((row) => (
                <TableRow key={row.engineId}>
                  <TableCell>
                    <div className="font-medium">{row.name}</div>
                    <div className="text-xs text-muted-foreground">{row.type} · {row.fuelRate} L/hr</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.hours.toFixed(1)}h</TableCell>
                  <TableCell className="text-right tabular-nums">{conv(row.liters, unit)} {unitLabel(unit)}</TableCell>
                  <TableCell className="text-right tabular-nums">${row.cost.toLocaleString()}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-bold border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">{data.totalHours.toFixed(0)}h</TableCell>
                <TableCell className="text-right tabular-nums">{conv(data.totalFuel, unit).toLocaleString()} {unitLabel(unit)}</TableCell>
                <TableCell className="text-right tabular-nums">${data.totalCost.toLocaleString()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Purchase log */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Fuel Purchases</CardTitle>
              <CardDescription>{periodPurchases.length} records · {periodLabels[period].toLowerCase()}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4" />
              Add Purchase
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add purchase form */}
          {showForm && (
            <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                <Input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Amount ({unitLabel(unit)})</label>
                <Input
                  type="number"
                  placeholder={`e.g. ${unit === "gallons" ? "500" : "1900"}`}
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-32"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Note</label>
                <Input
                  placeholder="e.g. Weekly delivery"
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="w-48"
                />
              </div>
              <Button size="sm" onClick={handleAddPurchase} disabled={!formAmount}>
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Amount ({unitLabel(unit)})</TableHead>
                <TableHead className="text-right">Cost (USD)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {periodPurchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No purchases recorded for this period
                  </TableCell>
                </TableRow>
              ) : (
                periodPurchases.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="tabular-nums">
                      {p.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{p.note}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {conv(p.liters, unit).toLocaleString()} {unitLabel(unit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      ${Math.round(p.liters * FUEL_PRICE_USD).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {periodPurchases.length > 0 && (
                <TableRow className="font-bold border-t-2">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {conv(totalPurchased, unit).toLocaleString()} {unitLabel(unit)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${Math.round(totalPurchased * FUEL_PRICE_USD).toLocaleString()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
